import { MutationCache, QueryCache, QueryClient } from "@tanstack/vue-query";
import { ZodError } from "zod";
import { reportContractBreach } from "../lib/contract-breach.ts";
import { ApiRequestError, InvalidInputError, UnauthenticatedError } from "./client.ts";

/** How long a read counts as fresh before a remount or focus fetches it again. */
export const STALE_TIME_MS = 30_000;

/**
 * Retries only what may pass on a second try: network failures and 5xx, at most twice. A 4xx
 * stays a 4xx, an answer that breaks its schema is a bug, not a hiccup (ADR-076 point 5), and an
 * input outside its schema was never sent and would not be the next time either (GP2).
 */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiRequestError && error.status < 500) return false;
  if (error instanceof ZodError || error instanceof InvalidInputError) return false;
  return failureCount < 2;
}

export type PortalQueryClientOptions = {
  /** Called when any read or change answers 401: the session is gone (ADR-076 point 5). */
  onUnauthenticated: () => void;
};

/** The route a query or change belongs to: the first part of its key (`routeKey`), else "unknown". */
const routeOf = (key: readonly unknown[] | undefined): string => (typeof key?.[0] === "string" ? key[0] : "unknown");

/**
 * The one cache for server data of the portal, with the defaults of ADR-076. Every failed read
 * and change passes here: a 401 ends the session, and an answer outside the contract is reported
 * (`reportContractBreach`) with the route from the key, once per route while the page lives, so
 * a lasting breach of the session, read again on every focus, does not report itself each time
 * (PF4).
 */
export function createPortalQueryClient({ onUnauthenticated }: PortalQueryClientOptions): QueryClient {
  const reported = new Set<string>();
  const onError = (error: unknown, key: readonly unknown[] | undefined) => {
    if (error instanceof UnauthenticatedError) onUnauthenticated();
    const route = routeOf(key);
    if (!reported.has(route) && reportContractBreach(error, route)) reported.add(route);
  };
  return new QueryClient({
    queryCache: new QueryCache({ onError: (error, query) => onError(error, query.queryKey) }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _result, mutation) => onError(error, mutation.options.mutationKey),
    }),
    defaultOptions: {
      queries: { staleTime: STALE_TIME_MS, retry: shouldRetry },
      mutations: { retry: false },
    },
  });
}
